// admin-flight.service.ts
import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'env/environment';
import { FirebaseService } from './firebase.service';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, writeBatch, setDoc } from 'firebase/firestore';
import { FlightOption } from 'app/models/flights.model';
import { FlightOptionService } from './flight-option-service';
import { getAuth } from 'firebase/auth';
import { DEFAULT_GROQ_CHAT_MODEL } from 'app/constants/ai-model-constants';

@Injectable({
  providedIn: 'root'
})
export class AdminFlightService {
  constructor(
    private firebaseService: FirebaseService,
    private http: HttpClient,
    private flightOptionService: FlightOptionService
  ) {}

  getTestFlightOptions(): Observable<FlightOption[]> {
    const flightOptionsRef = collection(this.firebaseService.firestore, 'flights');
    const q = query(flightOptionsRef, where('isTestOption', '==', true));
    return from(getDocs(q)).pipe(
      map(snapshot => snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FlightOption)))
    );
  }

  async updateFlightOption(option: FlightOption): Promise<void> {
    const { id, ...data } = option;
    if (!id) throw new Error('Missing flight option id.');
    const flightDocRef = doc(this.firebaseService.firestore, 'flights', id);
    await updateDoc(flightDocRef, data);
    await setDoc(
      doc(this.firebaseService.firestore, 'flight-search', id),
      this.flightOptionService.buildFlightSearchProjection(id, data),
      { merge: true }
    );
  }

  async deleteFlightOption(id: string): Promise<void> {
    const flightDocRef = doc(this.firebaseService.firestore, 'flights', id);
    await deleteDoc(flightDocRef);
    await deleteDoc(doc(this.firebaseService.firestore, 'flight-search', id)).catch(() => undefined);
  }

  async seedTestFlightOptions(params: {
    departureCities?: string[];
    arrivalCities?: string[];
    dateRangeStart?: string;
    dateRangeEnd?: string;
    numResults?: number;
  }): Promise<{ numAdded: number }> {
    const defaultParams = {
      dateRangeStart: new Date().toISOString().split('T')[0], // Today
      dateRangeEnd: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +2 weeks
      numResults: 50,
      model: DEFAULT_GROQ_CHAT_MODEL,
    };
    const seedParams = { ...defaultParams, ...params };
    const user = getAuth().currentUser;
    if (!user) {
      throw new Error('User not authenticated.');
    }
    const idToken = await user.getIdToken();

    const response = await this.http.post<{ numAdded: number }>(
      `https://${environment.apiBaseURL}.cloudfunctions.net/seedTestFlightOptions`,
      seedParams,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        }
      }
    ).toPromise();

    return response;
  }

  async refreshTestDataTiming(): Promise<void> {
    const flightOptionsRef = collection(this.firebaseService.firestore, 'flights');
    const q = query(flightOptionsRef, where('isTestOption', '==', true));
    const snapshot = await getDocs(q);

    const batch = writeBatch(this.firebaseService.firestore);
    const twoWeeksMs = 14 * 24 * 60 * 60 * 1000;

    snapshot.forEach(snapshotDoc => {
      const data = snapshotDoc.data() as FlightOption;
      const updatedData = { ...data };

      if (data.departureDateExact) {
        const newDate = new Date(data.departureDateExact);
        newDate.setTime(newDate.getTime() + twoWeeksMs);
        updatedData.departureDateExact = newDate.toISOString().split('T')[0];
      }
      if (data.departureDateRangeStart) {
        const newStart = new Date(data.departureDateRangeStart);
        newStart.setTime(newStart.getTime() + twoWeeksMs);
        updatedData.departureDateRangeStart = newStart.toISOString().split('T')[0];
      }
      if (data.departureDateRangeEnd) {
        const newEnd = new Date(data.departureDateRangeEnd);
        newEnd.setTime(newEnd.getTime() + twoWeeksMs);
        updatedData.departureDateRangeEnd = newEnd.toISOString().split('T')[0];
      }
      updatedData.updatedAt = new Date().toISOString();

      batch.update(snapshotDoc.ref, updatedData);
      batch.set(
        doc(this.firebaseService.firestore, 'flight-search', snapshotDoc.id),
        this.flightOptionService.buildFlightSearchProjection(snapshotDoc.id, updatedData),
        { merge: true }
      );
    });

    await batch.commit();
  }
}
